import { useState, useEffect, Component, type ReactNode } from 'react';
import { Sidebar } from './components/Sidebar';
import { LoginView } from './views/LoginView';
import { TrainerGuideView } from './views/TrainerGuideView';
import { DashboardView } from './views/DashboardView';
import { DailyProgressView } from './views/DailyProgressView';
import { AssessmentsView } from './views/AssessmentsView';
import { WeeklyRagView } from './views/WeeklyRagView';
import { AttendanceView } from './views/AttendanceView';
import { TrainingPlanView } from './views/TrainingPlanView';
import { CoursesView } from './views/CoursesView';
import { HypercareView } from './views/HypercareView';
import { AgentPortalView } from './views/AgentPortalView';

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: any }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error("App Error Boundary Caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 max-w-xl mx-auto mt-12 bg-red-50 border border-red-200 rounded-2xl text-red-900 space-y-4">
          <h2 className="text-lg font-bold">Runtime Rendering Error</h2>
          <pre className="text-xs font-mono bg-white p-4 rounded-xl border border-red-100 overflow-x-auto">
            {String(this.state.error?.stack || this.state.error)}
          </pre>
          <button
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
            className="px-4 py-2 bg-red-600 text-white font-bold rounded-xl text-xs cursor-pointer"
          >
            Clear LocalStorage & Reset App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  const [activeTab, setActiveTab] = useState('trainer-guide');
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('uisce-user-role'));

  useEffect(() => {
    const handleAuthChange = () => {
      setRole(localStorage.getItem('uisce-user-role'));
    };
    window.addEventListener('storage', handleAuthChange);
    return () => window.removeEventListener('storage', handleAuthChange);
  }, []);

  if (!role) {
    return <LoginView onLoginSuccess={() => setRole(localStorage.getItem('uisce-user-role'))} />;
  }

  if (role === 'agent') {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <AgentPortalView />
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'trainer-guide':
        return <TrainerGuideView />;
      case 'dashboard':
        return <DashboardView />;
      case 'daily-progress':
        return <DailyProgressView />;
      case 'assessments':
        return <AssessmentsView />;
      case 'weekly-rag':
        return <WeeklyRagView />;
      case 'attendance':
        return <AttendanceView />;
      case 'training-plan':
        return <TrainingPlanView />;
      case 'courses':
        return <CoursesView />;
      case 'hypercare':
        return <HypercareView />;
      default:
        return <TrainerGuideView />;
    }
  };

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-slate-100 overflow-hidden">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        <main className="flex-1 overflow-y-auto p-8">
          {renderContent()}
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default App;
